/**
 * Every address the console has, as two closed unions and a miss.
 *
 * The split is the point. Parsing takes the entry point as an argument, so on
 * the account domain `/catalog` is not a cluster address that happens to be
 * refused later — it is `notFound`, because on that host it does not exist. The
 * reverse holds on a tenancy domain for `/tenants`. Neither console can be
 * navigated into from the other by typing an address, and neither can be
 * reached by a bug in a role check, because there is no route to reach.
 *
 * Within the account the older rule still holds: the tenancy's own entries
 * carry no tenancy. Which tenancy a representative acts for follows from their
 * session, so there is deliberately nowhere in this type to put one taken from
 * a URL. The provider entries are different on purpose — an operator works
 * across tenancies, so `account.tenant` names one by slug, and the server still
 * decides whether this operator may see it.
 *
 * The slug is the identifier, here as everywhere: the control plane has no
 * opaque id for a tenancy, and inventing one for the address bar would be a
 * second name for the same thing.
 */

import type { EntryPoint } from '../entry/entryPoint';

export type AccountRoute =
  | { name: 'account.login' }
  | { name: 'account.tenants' }
  | { name: 'account.tenant'; slug: string }
  | { name: 'account.usage'; slug: string }
  /**
   * The version board. It carries no slug and could not: it is the fleet seen
   * at once, filtered to what the caller may see, and a tenancy in the address
   * would be a different screen.
   */
  | { name: 'account.rollout' }
  /**
   * The tenancy a representative acts for, and what it used.
   *
   * No slug, for the same reason `account.identityProvider` has none: which
   * tenancy this is follows from the session, and an address that named one
   * would be an address somebody could edit. They are not the operator's
   * `account.tenant` and `account.usage` with the slug left out — they are
   * different screens, showing what a customer owns rather than what a provider
   * operates.
   */
  | { name: 'account.tenancy' }
  | { name: 'account.tenancyUsage' }
  | { name: 'account.identityProvider' }
  | { name: 'account.password' };

export type ClusterRoute =
  | { name: 'cluster.login' }
  | { name: 'cluster.catalog' }
  | { name: 'cluster.nodes' }
  | { name: 'cluster.audit' };

export type Route = AccountRoute | ClusterRoute | { name: 'notFound' };

export type RouteName = Route['name'];

export interface Address {
  pathname: string;
  search: string;
}

const ACCOUNT_TENANT = /^\/tenants\/([^/]+)$/;
const ACCOUNT_USAGE = /^\/tenants\/([^/]+)\/usage$/;

export function parseRoute(entry: EntryPoint, address: Address): Route {
  const path = address.pathname.replace(/\/+$/, '') || '/';

  if (entry.kind === 'account') {
    if (path === '/login') return { name: 'account.login' };
    if (path === '/' || path === '/tenants') return { name: 'account.tenants' };
    if (path === '/rollout') return { name: 'account.rollout' };
    if (path === '/tenancy') return { name: 'account.tenancy' };
    if (path === '/tenancy/usage') return { name: 'account.tenancyUsage' };
    if (path === '/identity-provider') return { name: 'account.identityProvider' };
    if (path === '/password') return { name: 'account.password' };

    const usage = ACCOUNT_USAGE.exec(path);
    if (usage?.[1] !== undefined) {
      return { name: 'account.usage', slug: decodeURIComponent(usage[1]) };
    }

    const tenant = ACCOUNT_TENANT.exec(path);
    if (tenant?.[1] !== undefined) {
      return { name: 'account.tenant', slug: decodeURIComponent(tenant[1]) };
    }
    return { name: 'notFound' };
  }

  if (path === '/login') return { name: 'cluster.login' };
  if (path === '/' || path === '/catalog') return { name: 'cluster.catalog' };
  if (path === '/nodes') return { name: 'cluster.nodes' };
  if (path === '/audit') return { name: 'cluster.audit' };
  return { name: 'notFound' };
}

export function routeToPath(route: Route): string {
  switch (route.name) {
    case 'account.login':
    case 'cluster.login':
      return '/login';
    case 'account.tenants':
      return '/tenants';
    case 'account.tenant':
      return `/tenants/${encodeURIComponent(route.slug)}`;
    case 'account.usage':
      return `/tenants/${encodeURIComponent(route.slug)}/usage`;
    case 'account.rollout':
      return '/rollout';
    case 'account.tenancy':
      return '/tenancy';
    case 'account.tenancyUsage':
      return '/tenancy/usage';
    case 'account.identityProvider':
      return '/identity-provider';
    case 'account.password':
      return '/password';
    case 'cluster.catalog':
      return '/catalog';
    case 'cluster.nodes':
      return '/nodes';
    case 'cluster.audit':
      return '/audit';
    case 'notFound':
      return '/404';
  }
}

/**
 * Narrowing helpers, so each console's tree handles its own union and the
 * compiler checks that it handled all of it.
 */
export function asAccountRoute(route: Route): AccountRoute | null {
  switch (route.name) {
    case 'account.login':
    case 'account.tenants':
    case 'account.tenant':
    case 'account.usage':
    case 'account.rollout':
    case 'account.tenancy':
    case 'account.tenancyUsage':
    case 'account.identityProvider':
    case 'account.password':
      return route;
    default:
      return null;
  }
}

export function asClusterRoute(route: Route): ClusterRoute | null {
  switch (route.name) {
    case 'cluster.login':
    case 'cluster.catalog':
    case 'cluster.nodes':
    case 'cluster.audit':
      return route;
    default:
      return null;
  }
}
